import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, queryAll, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import DapDSIconButton from '../icon-button/icon-button.component'
import ArrowsExpandUpDownFill from '../icons/arrows/arrows-expand-up-down-fill/arrows-expand-up-down-fill.component'
import DapDSOptionItem from '../option-item/option-item.component'
import DapDSSelect from '../select/select.component'
import styles from './calendar.scss?inline'
import DapDSCalendarCell from './calendar-cell.component'

/**
 * `dap-ds-calendar`
 * @summary A calendar is a visual representation of dates. It allows users to select a date.
 * @element dap-ds-calendar
 * @title - Calendar
 *
 * @event {{ value: Dayjs }} dds-change - Fired when the calendar value changes.
 *
 * @slot - The content of the calendar.
 *
 * @csspart base - The main calendar container.
 * @csspart calendar-header - The header of the calendar.
 * @csspart body - The body of the calendar.
 * @csspart calendar-grid-header - The header of the calendar grid.
 * @csspart calendar-grid-header-cell - The header cell of the calendar grid.
 * @csspart calendar-grid-cell - The cell of the calendar grid.
 * @csspart calendar-header-button-prev - The previous month button of the calendar header.
 * @csspart calendar-header-button-next - The next month button of the calendar header.
 * @csspart calendar-header-year-select - The year select of the calendar header.
 * @csspart calendar-header-month-select - The month select of the calendar header.
 *
 * @cssproperty --dds-calendar-display - The display property of the calendar container (default: flex)
 * @cssproperty --dds-calendar-isolation - The isolation property of the calendar (default: isolate)
 * @cssproperty --dds-calendar-block-display - The display property of the calendar block (default: block)
 * @cssproperty --dds-calendar-transition - The transition property for the calendar (default: all 0.2s ease-in-out)
 * @cssproperty --dds-calendar-header-display - The display property of the calendar header (default: grid)
 * @cssproperty --dds-calendar-header-grid-flow - The grid-auto-flow property of the calendar header (default: column)
 * @cssproperty --dds-calendar-header-width - The width of the calendar header (default: 100%)
 * @cssproperty --dds-calendar-header-gap - The gap between header items (default: var(--dds-spacing-100))
 * @cssproperty --dds-calendar-select-min-height - The minimum height of the select trigger (default: auto)
 * @cssproperty --dds-calendar-select-max-height - The maximum height of the select trigger (default: 32px)
 * @cssproperty --dds-calendar-select-padding - The padding of the select trigger (default: var(--dds-spacing-200))
 * @cssproperty --dds-calendar-select-border-radius - The border radius of the select trigger (default: var(--dds-radius-rounded))
 * @cssproperty --dds-calendar-select-border-color - The border color of the select trigger (default: var(--dds-border-neutral-transparent-interactive))
 * @cssproperty --dds-calendar-select-background - The background color of the select trigger (default: var(--dds-button-subtle-background-enabled))
 * @cssproperty --dds-calendar-select-color - The text color of the select trigger (default: var(--dds-button-subtle-text-enabled))
 * @cssproperty --dds-calendar-select-font-size - The font size of the select trigger (default: var(--dds-font-sm))
 * @cssproperty --dds-calendar-select-font-style - The font style of the select trigger (default: normal)
 * @cssproperty --dds-calendar-select-font-weight - The font weight of the select trigger (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-calendar-nav-button-margin - The margin of the navigation buttons (default: var(--dds-spacing-200))
 * @cssproperty --dds-calendar-nav-button-color - The color of the navigation button icons (default: var(--dds-button-subtle-icon-enabled))
 */
export default class DapDSCalendar extends DdsElement {
  static tagName = 'dap-ds-calendar'

  static dependencies = {
    'dap-ds-calendar-cell': DapDSCalendarCell,
    'dap-ds-icon-button': DapDSIconButton,
    'dap-ds-select': DapDSSelect,
    'dap-ds-icon-expand-up-down-fill': ArrowsExpandUpDownFill,
    'dap-ds-option-item': DapDSOptionItem,
  }

  @queryAll('dap-ds-calendar-cell:not([header])')
  private readonly cells: NodeListOf<DapDSCalendarCell>

  @query('#monthSelect')
  private readonly monthSelect: DapDSSelect

  /** The mode of the calendar - single date or range selection.
   * @type {'single' | 'range'}
   * @default 'single'
   */
  @property({ reflect: true }) mode: 'single' | 'range' = 'single'

  /** Whether to hide dates from adjacent months (previous/next) in the calendar grid. When not explicitly set, defaults to false for single mode and true for range mode.*/
  @property({ type: Boolean }) hideAdjacentMonths: boolean = false

  /** The value of the calendar.
   * @type {Dayjs}
   */
  @property({ attribute: false }) value?: Dayjs

  /** The start date of the range selection (only used in range mode).
   * @type {Dayjs}
   */
  @property({ attribute: false }) rangeStart?: Dayjs

  /** The end date of the range selection (only used in range mode).
   * @type {Dayjs}
   */
  @property({ attribute: false }) rangeEnd?: Dayjs

  /** The current visible date of the calendar. Only the month and year are considered.
   * @type {Dayjs}
   * @default dayjs()
   */
  @property({ attribute: false }) currentDate = dayjs()

  /** The minimum date of the calendar. Only the month and year are considered.
   * @type {Dayjs}
   * @default dayjs().subtract(50, 'year')
   */
  @property({ attribute: false }) minDate = dayjs().subtract(50, 'year')

  /** The maximum date of the calendar. Only the month and year are considered.
   * @type {Dayjs}
   * @default dayjs().add(50, 'year')
   */
  @property({ attribute: false }) maxDate = dayjs().add(50, 'year')

  /** The function to determine if the date is disabled.
   * @type {Function}
   * @default () => false
   */
  @property({ attribute: false }) disabledDate = (date: Dayjs) => {
    return !date
  }

  /** The locale of the calendar.
   * @type {'hu' | 'en' | 'de'}
   * @default dayjs.locale()
   */
  @property({ reflect: true }) locale: string = dayjs.locale()

  /** `data-testid` for the previous-month button. */
  @property() prevButtonTestId = 'calendar-prev-button'

  /** `data-testid` for the next-month button. */
  @property() nextButtonTestId = 'calendar-next-button'

  @state() private _currentCell: DapDSCalendarCell
  @state() private _yearOptionsLoaded = false

  private instanceLocalData = dayjs().localeData()

  async connectedCallback(): Promise<void> {
    super.connectedCallback()

    if (!dayjs.isDayjs(this.currentDate)) {
      this.currentDate = dayjs(this.currentDate)
    }

    if (this.locale) {
      this.instanceLocalData = dayjs().locale(this.locale).localeData()
      this.currentDate = dayjs(this.currentDate).locale(this.locale)
      // Remove requestUpdate() - let Lit handle the update cycle
    }
  }

  static readonly styles = unsafeCSS(styles)

  get startDate() {
    if (dayjs.isDayjs(this.currentDate)) {
      return dayjs(this.currentDate.toDate())
        .locale(this.locale)
        .startOf('month')
    }
    return dayjs(this.currentDate).locale(this.locale).startOf('month')
  }

  get endDate() {
    return dayjs(this.currentDate).locale(this.locale).endOf('month')
  }

  get shouldHideAdjacentMonths() {
    // If explicitly set, use that value
    if (this.mode === 'range') {
      return true
    }
    // Default: false for single mode, true for range mode
    return this.hideAdjacentMonths
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    const documentElementObserver = new MutationObserver(
      this.updateLocaleFromHtml,
    )

    documentElementObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir', 'lang'],
    })

    this.setValue(this.value, false)

    // Load year options after initial render to improve startup performance
    // but ensure they're available for user interaction
    queueMicrotask(() => this.loadYearOptions())
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('value')) {
      this.setValue(this.value, false)
    }
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value)

    if (name === 'locale') {
      this.updateLocale(value)
    }
  }

  private readonly updateLocale = (locale?: string | null) => {
    if (!locale) {
      this.locale = dayjs.locale()
    } else {
      this.instanceLocalData = dayjs().locale(locale).localeData()
      this.locale = locale

      // Defer DOM manipulation to avoid update cycles
      this.updateComplete.then(() => {
        this.monthSelect
          ?.querySelectorAll('dap-ds-option-item')
          .forEach(item => item.remove())

        const monthOptions = this.getAllowedMonths(
          dayjs(this.currentDate).locale(this.locale),
        ).map(month => {
          const option = document.createElement('dap-ds-option-item')
          option.value = `${this.instanceLocalData.months().indexOf(month) + 1}`
          option.appendChild(document.createTextNode(month))
          return option
        })

        monthOptions.forEach(monthOption =>
          this.monthSelect?.appendChild(monthOption),
        )
      })
    }
    // Remove manual requestUpdate() - locale property change will trigger update
  }

  private readonly updateLocaleFromHtml = (mutationList: any) => {
    this.updateLocale(mutationList[0].target[mutationList[0].attributeName])
  }

  setValue(value?: Dayjs, setFocus = true) {
    if (value) {
      if (
        this.isOutOfRange(
          dayjs(this.currentDate).locale(this.locale),
          dayjs(value),
        )
      ) {
        this.setToFirstDayOfMonth()
      } else {
        const selectedCell = this.getCellByDate(dayjs(value))
        this.setCurrentCell(selectedCell!, setFocus)
      }
    } else if (
      this.isOutOfRange(dayjs(this.currentDate).locale(this.locale), dayjs())
    ) {
      this.setToFirstAllowedDayOfMonth()
    } else {
      this.setToToday()
    }
  }

  private readonly getWeeksInMonth = (date: Dayjs) => {
    const days = date.daysInMonth()
    const weekday = date.weekday()

    return Math.ceil((days + weekday) / 7)
  }

  private loadYearOptions() {
    if (!this._yearOptionsLoaded) {
      this._yearOptionsLoaded = true
      // Immediate update for better UX
      this.requestUpdate()
    }
  }

  isOutOfRange(currentDate: Dayjs, date: Dayjs) {
    return !dayjs(currentDate).locale(this.locale).isSame(date, 'month')
  }

  isToday(date: Dayjs) {
    return date.isSame(new Date(), 'day')
  }

  isSelected(date: Dayjs) {
    if (this.mode === 'range') {
      return this.isRangeStart(date) || this.isRangeEnd(date)
    }

    if (!this.value) {
      return false
    }

    return date.isSame(this.value, 'day')
  }

  isRangeStart(date: Dayjs) {
    if (this.mode !== 'range' || !this.rangeStart) {
      return false
    }
    return date.isSame(this.rangeStart, 'day')
  }

  isRangeEnd(date: Dayjs) {
    if (this.mode !== 'range' || !this.rangeEnd) {
      return false
    }
    return date.isSame(this.rangeEnd, 'day')
  }

  isInRange(date: Dayjs) {
    if (this.mode !== 'range' || !this.rangeStart || !this.rangeEnd) {
      return false
    }
    return (
      date.isAfter(this.rangeStart, 'day') &&
      date.isBefore(this.rangeEnd, 'day')
    )
  }

  getDatesInWeek(week: number, from: Dayjs) {
    let date = from.startOf('week').add(week, 'week')
    const dates: Dayjs[] = []

    while (dates.length < 7) {
      dates.push(date)
      const nextDate = date.add(1, 'day')
      if (date.isSame(from.endOf('month'))) {
        break
      }
      date = nextDate
    }

    return dates
  }

  isDateInAllowedRange(date: Dayjs) {
    const minDate = dayjs(this.minDate).locale(this.locale)
    const maxDate = dayjs(this.maxDate).locale(this.locale)
    return !date.isBefore(minDate, 'day') && !date.isAfter(maxDate, 'day')
  }

  isMonthInAllowedRange(year: number, month: number) {
    const monthStart = dayjs(new Date(year, month, 1)).locale(this.locale)
    const monthEnd = monthStart.endOf('month')
    const minDate = dayjs(this.minDate).locale(this.locale)
    const maxDate = dayjs(this.maxDate).locale(this.locale)

    // A month is allowed if it intersects the [minDate, maxDate] range.
    return (
      !monthEnd.isBefore(minDate, 'day') && !monthStart.isAfter(maxDate, 'day')
    )
  }

  isDateInAllowedRangeByDate(year: number, month: number, date: number) {
    return this.isDateInAllowedRange(dayjs(new Date(year, month, date)))
  }

  getAllowedMonths(currentDate: Dayjs) {
    const minDate = dayjs(this.minDate).locale(this.locale)
    const maxDate = dayjs(this.maxDate).locale(this.locale)
    const minYear = minDate.year()
    const maxYear = maxDate.year()
    const minMonth = minDate.month()
    const maxMonth = maxDate.month()

    if (currentDate.year() === minYear && currentDate.year() === maxYear) {
      return Array.from(
        { length: maxMonth - minMonth + 1 },
        (_, i) => this.instanceLocalData.months()[i + minMonth],
      )
    }

    if (currentDate.year() === minYear) {
      return Array.from(
        { length: 12 - minMonth },
        (_, i) => this.instanceLocalData.months()[i + minMonth],
      )
    }

    if (currentDate.year() === maxYear) {
      return Array.from(
        { length: maxMonth + 1 },
        (_, i) => this.instanceLocalData.months()[i],
      )
    }

    const months = Array.from(
      { length: 12 },
      (_, i) => this.instanceLocalData.months()[i],
    )

    return months
  }

  setMonth(month: number) {
    if (
      month < 0 &&
      this.isMonthInAllowedRange(dayjs(this.currentDate).year() - 1, 11)
    ) {
      this.setYear(dayjs(this.currentDate).locale(this.locale).year() - 1)
      this.currentDate = this.currentDate.locale(this.locale).set('month', 11)
      return
    }

    if (
      month > 11 &&
      this.isMonthInAllowedRange(
        dayjs(this.currentDate).locale(this.locale).year() + 1,
        0,
      )
    ) {
      this.setYear(dayjs(this.currentDate).year() + 1)
      this.currentDate = this.currentDate.set('month', 0)
      return
    }

    if (
      !this.isMonthInAllowedRange(
        dayjs(this.currentDate).locale(this.locale).year(),
        month,
      )
    ) {
      return
    }

    this.currentDate = dayjs(this.currentDate)
      .locale(this.locale)
      .set('month', month)
    // Remove requestUpdate() - currentDate property change will trigger update
  }

  setDay(day: number) {
    const date = dayjs(this.currentDate).locale(this.locale)

    if (!this.isDateInAllowedRangeByDate(date.year(), date.month(), day)) {
      return
    }

    this.currentDate = date.set('date', day)
    // Remove requestUpdate() - currentDate property change will trigger update
  }

  setYear(year: number) {
    const date = dayjs(this.currentDate).locale(this.locale)

    this.currentDate = date.set('year', year)
    // Remove requestUpdate() - currentDate property change will trigger update
  }

  setSelectedDate(date: Dayjs) {
    if (this.disabledDate(date)) {
      return
    }

    if (!this.isDateInAllowedRange(date)) {
      return
    }

    if (this.isOutOfRange(dayjs(this.currentDate).locale(this.locale), date)) {
      this.setMonth(date.month())
    }

    if (this.mode === 'range') {
      // Range selection logic
      if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
        // Start new range selection
        this.rangeStart = date
        this.rangeEnd = undefined
        // Update currentDate to show the start date's month
        this.currentDate = dayjs(date).locale(this.locale)
      } else if (this.rangeStart && !this.rangeEnd) {
        // Complete range selection
        if (date.isBefore(this.rangeStart)) {
          // If end date is before start date, swap them
          this.rangeEnd = this.rangeStart
          this.rangeStart = date
        } else {
          this.rangeEnd = date
        }

        // Update currentDate to show the start date's month in the first calendar
        this.currentDate = dayjs(this.rangeStart).locale(this.locale)

        // Emit range-change event
        this.emit('dds-range-change', {
          startDate: this.rangeStart,
          endDate: this.rangeEnd,
        })
      }

      this.setCurrentCell(this.getCellByDate(date)!)
    } else {
      // Single date selection
      this.value = date
      this.setCurrentCell(this.getCellByDate(date)!)

      this.emit('dds-change', {
        value: this.value,
        type: 'value',
      })
    }
  }

  getFirstDayOfMonthCell() {
    return [...this.cells].find(cell => {
      return dayjs(cell.value).locale(this.locale).isSame(this.startDate, 'day')
    })
  }

  getCellByDate(date: Dayjs) {
    return [...this.cells].find(cell => {
      return dayjs(cell.value).locale(this.locale).isSame(date, 'day')
    })
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      return
    }

    event.preventDefault()
    const element = event.composedPath()[0]
    if (element instanceof DapDSCalendarCell) {
      switch (event.code) {
        case 'ArrowLeft':
          this.focusNextCell(element, -1)
          break
        case 'ArrowRight':
          this.focusNextCell(element, 1)
          break
        case 'ArrowUp':
          this.focusNextCell(element, -7)
          break
        case 'ArrowDown':
          this.focusNextCell(element, 7)
          break
        case 'PageUp':
          this.setMonth(dayjs(this.currentDate).locale(this.locale).month() - 1)
          break
        case 'PageDown':
          this.setMonth(dayjs(this.currentDate).locale(this.locale).month() + 1)
          break
        case 'Enter':
          this.setSelectedDate(element.value)
          break
        case 'Escape':
          this.emit('dds-close')
          break
      }
    } else if (event.key === 'Escape') {
      this.emit('dds-close')
    }
  }

  setCurrentCell(cell: DapDSCalendarCell, setFocus = true) {
    if (this._currentCell) {
      this._currentCell.setAttribute('tabindex', '-1')
    }
    this._currentCell = cell
    if (this._currentCell) {
      this._currentCell.setAttribute('tabindex', '0')
    }

    if (setFocus && this._currentCell) {
      this.setFocusToCurrentCell()
    }
  }

  setFocusToCurrentCell() {
    this._currentCell.focus()
  }

  setToFirstDayOfMonth() {
    const firstDayofMonth = this.getFirstDayOfMonthCell()
    firstDayofMonth?.setAttribute('tabindex', '0')
    this._currentCell = firstDayofMonth!
  }

  setToToday() {
    const today = this.getCellByDate(dayjs())
    today?.setAttribute('tabindex', '0')
    this._currentCell = today!
  }

  setToFirstAllowedDayOfMonth(currentDate = this.currentDate) {
    const firstAllowedDay = this.getCellByDate(
      dayjs(currentDate).locale(this.locale).startOf('month'),
    )
    firstAllowedDay?.setAttribute('tabindex', '0')
    this._currentCell = firstAllowedDay!
  }

  focusNextCell(element: DapDSCalendarCell, offset: number) {
    const index = [...this.cells].indexOf(element)
    const nextIndex = index + offset
    const currentMonth = dayjs(this.currentDate).locale(this.locale).month()

    if (nextIndex >= 0 && nextIndex < this.cells.length) {
      const cell = this.cells[nextIndex]
      this.setCurrentCell(cell)
    } else if (nextIndex < 0) {
      this.setMonth(currentMonth - 1)

      if (this.currentDate.month() < currentMonth) {
        this.updateComplete.then(() => {
          const lastDay = dayjs(this.currentDate)
            .locale(this.locale)
            .endOf('month')
          const lastDayCell = this.getCellByDate(lastDay)
          this.setCurrentCell(lastDayCell!)
        })
      }
    } else if (nextIndex >= this.cells.length) {
      this.setMonth(currentMonth + 1)

      if (this.currentDate.month() > currentMonth) {
        this.updateComplete.then(() => {
          const firstDay = dayjs(this.currentDate)
            .locale(this.locale)
            .startOf('month')
          const firstDayCell = this.getCellByDate(firstDay)
          this.setCurrentCell(firstDayCell!)
        })
      }
    }
  }

  public captureFocus(): void {
    this.focus()
    this.setFocusToCurrentCell()
  }

  private getWeekdaysMin() {
    const firstDay = this.instanceLocalData.firstDayOfWeek()
    const weekdays = this.instanceLocalData.weekdaysMin()
    return weekdays.slice(firstDay).concat(weekdays.slice(0, firstDay))
  }

  renderCalendarGridHeader() {
    return html`<thead part="calendar-grid-header" class="calendar-grid-header">
      <tr class="calendar-grid-header-row">
        ${this.getWeekdaysMin().map(
          day =>
            html`<th class="calendar-cell">
              <dap-ds-calendar-cell
                header
                part="calendar-grid-header-cell"
                exportparts="base: calendar-grid-header-cell-base"
                locale=${this.locale}
                >${day}</dap-ds-calendar-cell
              >
            </th>`,
        )}
      </tr>
    </thead>`
  }

  renderCalendarGridBody(date: Dayjs) {
    return html`<tbody part="body" class="calendar-grid-body">
      ${[
        ...new Array(
          this.getWeeksInMonth(date || dayjs().locale(this.locale)),
        ).keys(),
      ].map(
        week =>
          html`<tr class="calendar-grid-row">
            ${this.getDatesInWeek(week, date).map(d => {
              const isOutOfRange = this.isOutOfRange(date, d)
              const isDisabled =
                !this.isDateInAllowedRange(d) || this.disabledDate(d)

              const isRangeStart = this.isRangeStart(d)
              const isRangeEnd = this.isRangeEnd(d)
              const isInRange = this.isInRange(d)

              // Hide dates from adjacent months if shouldHideAdjacentMonths is true
              if (this.shouldHideAdjacentMonths && isOutOfRange) {
                return html`<td
                  role="gridcell"
                  class="${clsx('calendar-cell calendar-cell--empty')}"></td>`
              }

              return html`<td
                role="gridcell"
                class="${clsx('calendar-cell')}"
                aria-disabled=${isDisabled}>
                <dap-ds-calendar-cell
                  part="calendar-grid-cell"
                  exportparts="base: calendar-grid-cell-base, today-mark: calendar-grid-cell-today-mark"
                  ?outofrange=${isOutOfRange}
                  ?today=${this.isToday(d)}
                  ?selected=${this.isSelected(d)}
                  ?rangestart=${isRangeStart}
                  ?rangeend=${isRangeEnd}
                  ?inrange=${isInRange}
                  .value=${d}
                  locale=${this.locale}
                  ?disabledDay=${isDisabled}
                  role="button"
                  aria-pressed=${this.isSelected(d)}
                  aria-disabled=${isDisabled}
                  @click=${() => !isDisabled && this.setSelectedDate(d)}
                  @keydown=${this.handleKeyDown}
                  >${d.date()}</dap-ds-calendar-cell
                >
              </td>`
            })}
          </tr> `,
      )}
    </tbody>`
  }

  renderCalendarHeader() {
    return html`
      <header
        part="calendar-header"
        class="calendar__header"
        @keydown=${this.handleKeyDown}>
        <dap-ds-icon-button
          part="calendar-header-button-prev"
          class="calendar__header-button-prev"
          data-testid=${this.prevButtonTestId}
          clean
          size="lg"
          variant="brand"
          icon="arrow-left-l-line"
          @keydown=${this.handleKeyDown}
          aria-label=${t('calendar.prevMonth', { lng: this.locale })}
          @click=${() => {
            this.setMonth(this.currentDate.month() - 1)
          }}>
        </dap-ds-icon-button>
        <dap-ds-select
          part="calendar-header-year-select"
          floatingStrategy="fixed"
          class=${clsx(
            'calendar__header-select',
            'calendar__header-year-select',
          )}
          @dds-keydown=${this.handleKeyDown}
          size="sm"
          noAnimation
          aria-label=${t('calendar.year', { lng: this.locale })}
          value=${dayjs(this.currentDate).year()}
          @dds-change=${(e: CustomEvent) => {
            e.preventDefault()
            e.stopPropagation()
            if (
              e.detail.value &&
              !isNaN(parseInt(e.detail.value)) &&
              parseInt(e.detail.value) !==
                dayjs(this.currentDate).locale(this.locale).year()
            ) {
              this.currentDate = dayjs(this.currentDate)
                .locale(this.locale)
                .set('year', parseInt(e.detail.value))
              // Remove requestUpdate() - currentDate property change will trigger update
            }
          }}>
          <dap-ds-icon-expand-up-down-fill
            slot="indicator-icon"
            size="16"></dap-ds-icon-expand-up-down-fill>
          ${this._yearOptionsLoaded
            ? [
                ...new Array(
                  dayjs(this.maxDate).locale(this.locale).year() -
                    dayjs(this.minDate).locale(this.locale).year() +
                    1,
                ).keys(),
              ].map(
                year =>
                  html`<dap-ds-option-item
                    value=${dayjs(this.minDate).locale(this.locale).year() +
                    year}>
                    ${dayjs(this.minDate).locale(this.locale).year() + year}
                  </dap-ds-option-item>`,
              )
            : html`<dap-ds-option-item value=${dayjs(this.currentDate).year()}>
                ${dayjs(this.currentDate).year()}
              </dap-ds-option-item>`}
        </dap-ds-select>
        <dap-ds-select
          id="monthSelect"
          part="calendar-header-month-select"
          floatingStrategy="fixed"
          class=${clsx(
            'calendar__header-select',
            'calendar__header-month-select',
          )}
          size="sm"
          noAnimation
          aria-label=${t('calendar.month', { lng: this.locale })}
          value=${dayjs(this.currentDate).locale(this.locale).month() + 1}
          @dds-change=${(e: CustomEvent) => {
            e.preventDefault()
            e.stopPropagation()

            if (
              e.detail.value &&
              !isNaN(parseInt(e.detail.value)) &&
              parseInt(e.detail.value) !==
                dayjs(this.currentDate).locale(this.locale).month() + 1
            ) {
              this.setMonth(parseInt(e.detail.value) - 1)
            }
          }}>
          <dap-ds-icon-expand-up-down-fill
            slot="indicator-icon"
            size="16"></dap-ds-icon-expand-up-down-fill>
          ${this.getAllowedMonths(
            dayjs(this.currentDate).locale(this.locale),
          ).map(
            month =>
              html`<dap-ds-option-item
                value=${this.instanceLocalData.months().indexOf(month) + 1}>
                ${month}
              </dap-ds-option-item>`,
          )}
        </dap-ds-select>
        <dap-ds-icon-button
          part="calendar-header-button-next"
          class="calendar__header-button-next"
          data-testid=${this.nextButtonTestId}
          clean
          size="lg"
          variant="brand"
          icon="arrow-right-l-line"
          aria-label=${t('calendar.nextMonth', { lng: this.locale })}
          @click=${() => {
            this.setMonth(
              dayjs(this.currentDate).locale(this.locale).month() + 1,
            )
          }}>
        </dap-ds-icon-button>
      </header>
    `
  }

  render() {
    if (this.mode === 'range') {
      // Render two calendars side-by-side for range mode
      const currentMonth = dayjs(this.currentDate).locale(this.locale)
      const nextMonth = currentMonth.add(1, 'month')

      return html` <div class="calendar calendar--range">
        ${this.renderCalendarHeader()}
        <div class="calendar__months">
          <div class="calendar__month">
            <table
              part="base"
              class="calendar__grid"
              role="grid"
              aria-label=${`${this.rangeStart ? dayjs(this.rangeStart).locale(this.locale).format('LL, dddd') : ''}`}>
              ${this.renderCalendarGridHeader()}
              ${this.renderCalendarGridBody(this.startDate)}
            </table>
          </div>
          <div class="calendar__month calendar__month--next">
            <table
              part="base"
              class="calendar__grid"
              role="grid"
              aria-label=${`${this.rangeEnd ? dayjs(this.rangeEnd).locale(this.locale).format('LL, dddd') : ''}`}>
              ${this.renderCalendarGridHeader()}
              ${this.renderCalendarGridBody(nextMonth.startOf('month'))}
            </table>
          </div>
        </div>
      </div>`
    }

    // Single date mode
    return html` <div class="calendar">
      ${this.renderCalendarHeader()}
      <table
        part="base"
        class="calendar__grid"
        role="grid"
        aria-label=${`${this.value ? dayjs(this.value).locale(this.locale).format('LL, dddd') : ''}`}>
        ${this.renderCalendarGridHeader()}
        ${this.renderCalendarGridBody(this.startDate)}
      </table>
    </div>`
  }
}
